import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { unwrap } from '@/lib/result';

import { configRepository } from '../api/config.repository';
import {
  flag as readFlag,
  num as readNum,
  toSettingsMap,
  type BoolSettingKey,
  type NumberSettingKey,
} from '../model/app-setting';

export const configKeys = {
  all: ['config'] as const,
};

/**
 * Reads every app_settings row once (React Query cache, shared across every
 * caller) and exposes `flag`/`num` selectors bound to the current data —
 * each falls back to the REGISTRY default while the query is loading or if a
 * key is ever missing, so call sites never need their own loading branch.
 *
 * The Realtime subscription that keeps this cache live is centralized in
 * `app/ConfigProvider.tsx` (mounted once at the app root) rather than here,
 * so opening many components that call `useAppSettings()` doesn't open one
 * `postgres_changes` channel per component — they all just read the same
 * query-cache key, which the provider invalidates on change.
 */
export function useAppSettings() {
  const query = useQuery({
    queryKey: configKeys.all,
    queryFn: () => unwrap(configRepository.getSettings()),
    select: toSettingsMap,
  });

  return {
    ...query,
    flag: (key: BoolSettingKey) => readFlag(query.data, key),
    num: (key: NumberSettingKey) => readNum(query.data, key),
  };
}

export function useSetSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { key: string; value: unknown }) =>
      unwrap(configRepository.setSetting(vars.key, vars.value)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: configKeys.all });
    },
  });
}
